import { Anchor, Box, Heading, Tag } from '@zillow/constellation-10';
import { event } from '@zillow/universal-analytics';
import { yieldCallback } from '@zillow/yield-callback';
import { getCmsInfoForPage, usePageProps } from '@zillow/zrs-cms';
import React from 'react';

import { BaseTerm } from '../../types/shared';

export interface TagsProps {
    /** WordPress post tags. `BaseTerm` is already the shape the callers pass. */
    postTags?: BaseTerm[];
}

const Tags = ({ postTags }: TagsProps): JSX.Element | null => {
    // v8 read `theme.constellation.spacing.lg` through styled-components'
    // useTheme() to build the padding. That direct v8 theme access is gone —
    // the value is now the `looser` Panda token (see below).
    const pageProps = usePageProps();

    if (!postTags || postTags.length === 0) {
        return null;
    }

    return (
        <Box
            css={{
                // v8 `padding: ${theme.constellation.spacing.lg}px 0` = 32px 0.
                // $spacing.lg = 32px = spacing.800 = `looser`.
                paddingBlock: 'looser',
                paddingInline: '0',
            }}
        >
            {/* v10's Heading maps level 5 to textStyle `heading-xs` by default,
                which is exactly v8's h5 (20px / 24px line-height), so no
                textStyle override is needed. v8 marginBottom="sm" = 16px =
                spacing.400 = `default`. */}
            <Heading level={5} css={{ marginBottom: 'default' }}>
                Tags
            </Heading>

            <Box
                css={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    // v8 hardcoded 12px because it fell between $spacing.xs (8)
                    // and $spacing.sm (16). v10 has that step as a real token:
                    // `tight` = spacing.300 = 0.75rem = 12px.
                    gap: 'tight',
                }}
            >
                {postTags.map((postTag) => (
                    <Anchor
                        key={postTag.id}
                        href={postTag.link}
                        // Replaces the v8 styled(Anchor) wrapper.
                        css={{ textDecoration: 'none' }}
                        onClick={yieldCallback(() => {
                            event({
                                // https://dataportal.zillowgroup.net/event-registry/2048
                                envelope: {
                                    event_type_id: '2048',
                                    event_type_version_id: '1',
                                    event_template_id: '126',
                                    event_template_version_id: '1',
                                    // Master ID: 2284.2.126.1
                                },
                                clickstream_trigger: {
                                    trigger_type_nm: 'interaction',
                                    trigger_location_nm: 'cms',
                                    trigger_source_nm: 'cms_tag',
                                    trigger_object_nm: 'no_trigger_object',
                                },
                                semantic: {
                                    semantic_event_nm: 'cms_click',
                                },
                                cms_info: {
                                    block_id: postTag.id || '',
                                    block_type_cd: 'tag',
                                    block_displayed_txt: postTag.name,
                                    block_target_url: postTag.link,
                                    block_target_window_txt: '',
                                    // `usePageProps()` is typed
                                    // `Record<string, unknown> | undefined`
                                    // while `getCmsInfoForPage` takes an
                                    // `object`. It returns `{}` for a falsy
                                    // argument, and `{}` for an object with
                                    // none of the keys it looks for, so the
                                    // fallback is behaviour-preserving — it
                                    // only surfaced now that this file is
                                    // typechecked.
                                    ...getCmsInfoForPage(pageProps ?? {}),
                                },
                            });
                        })}
                    >
                        {/* v8 needed textTransform="none" because v8's Tag
                            defaulted to `textTransform: 'uppercase'`. v10's Tag
                            has no textTransform at all, so the opt-out is
                            dropped rather than re-expressed. */}
                        <Tag>
                            {/* dangerouslySetInnerHTML cannot go on Tag itself —
                             * it renders children into Tag.Label, so React
                             * rejects having both. */}
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: postTag.name,
                                }}
                            />
                        </Tag>
                    </Anchor>
                ))}
            </Box>
        </Box>
    );
};

export default Tags;
